import { supabase } from '@/lib/supabase';
import type { DivisionWithCourses } from '@/lib/types';
import { isObjectEmpty } from '@/utils';

import { getCourseMap, updateCourseMap } from '../queries/useCourseMap';

const schoolId = import.meta.env.VITE_SCHOOL_ID;

// Helper function to recursively update division names in course map
const eachRecursive = (obj: any, previousName = '', newName = '') => {
	// eslint-disable-next-line
	for (const k in obj) {
		if (Object.hasOwn(obj, k)) {
			if (typeof obj[k] === 'object' && obj[k] !== null) {
				eachRecursive(obj[k], previousName, newName);
			}

			if (k === 'division' && obj[k] === previousName) {
				obj[k] = newName;
			}
		}
	}
	return obj;
};

// Update course map when division name changes
const updateCourseMapDivisionName = async (previousName: string, newName: string) => {
	const mapJSON = await getCourseMap();

	if (isObjectEmpty(mapJSON)) {
		return { affected_rows: 0 };
	}
	return updateCourseMap({ map: eachRecursive(mapJSON, previousName, newName) });
};

export interface DivisionsMutation {
	id: string;
	name: string;
	previousState: any;
}

export const deleteDivision = async (variables: { id: string }) => {
	const { id } = variables;

	const { data, error } = await supabase
		.from('divisions')
		.delete()
		.eq('id', id)
		.eq('school_id', schoolId)
		.select('id')
		.single();

	if (error) throw error;

	return data;
};

export const updateCourseDivision = async (variables: {
	previousDivision: {
		id: string;
		name: string | null;
	};
	newDivision: {
		id: string;
		name: string;
	};
}) => {
	const { previousDivision, newDivision } = variables;

	// Update all courses_divisions records to point to the new division
	const { data, error } = await supabase
		.from('courses_divisions')
		.update({ division_id: newDivision.id })
		.eq('division_id', previousDivision.id)
		.select('course_id, courses_divisions_id, division_id');

	if (error) throw error;

	// Delete the old division
	await deleteDivision({
		id: previousDivision.id,
	});

	// Update course map if names are different
	if (previousDivision?.name !== newDivision.name && previousDivision?.name) {
		await updateCourseMapDivisionName(previousDivision.name ?? '', newDivision.name ?? '');
	}

	return data;
};

export const updateDivisions = async (variables: DivisionsMutation) => {
	const { id, name, previousState } = variables;

	// Update course map if name changed
	if (previousState?.name !== name && previousState) {
		await updateCourseMapDivisionName(previousState.name ?? '', name);
	}

	// Update the division
	const { data, error } = await supabase
		.from('divisions')
		.update({ name })
		.eq('id', id)
		.eq('school_id', schoolId)
		.select(
			`
			id,
			name,
			courses_divisions (
				courses_divisions_id,
				course_id,
				division_id
			)
		`
		)
		.single();

	if (error) throw error;

	// Ensure return type matches expected format
	return {
		id: data.id,
		name: data.name,
		courses_divisions: data.courses_divisions ?? [],
	} as any;
};

export const createDivision = async (variables: { name: string }) => {
	const { name } = variables;

	const { data, error } = await supabase
		.from('divisions')
		.insert({
			name,
			school_id: schoolId,
		})
		.select('name, id')
		.single();

	if (error) throw error;

	// Return in the format expected by the component
	return {
		id: data.id,
		name: data.name,
		courses_divisions: [],
	} as unknown as DivisionWithCourses;
};
